import { useEffect, useMemo, useRef, useState, type FormEvent, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { AppButton } from "@/components/ui/primitives";
import { FiChevronRight } from "react-icons/fi";
import { ModalShell } from "@/components/ui/ModalShell";
import {
  type SavingInstitution,
  type SavingPocket,
  useDeleteSavingInstitution,
  useDeleteSavingPocket,
  useSavingInstitutions,
  useSavingPockets,
  useUpdateSavingInstitution,
  useUpdateSavingPocket,
} from "@/hooks/useSavingPockets";
import { formatCurrency, formatDate, formatPercent } from "@/utils/formatters";
import { confirmAction } from "@/components/ui/confirmDialog";
import { PageSkeleton } from "@/components/ui/PageSkeleton";
import {
  getEarliestFreezeDate,
  getLatestUnfreezeDate,
  getReturnAfterFreeze,
  getUnfreezeDate,
  buildSavingPocketRows,
  buildUnifiedSavingPocketRows,
  getSavingPocketOverview,
  parseDateValue,
  parseFreezeTime,
  splitSavingPocketRows,
  type SavingPocketDisplayRow,
  type SavingPocketListRow,
} from "../utils/savingPocketCalculations";
import { Field, FREEZE_PRESET_OPTIONS, FreezeTimeField, SelectField, ToggleField, type FreezePreset } from "../components/SavingPocketFields";
import { EmptyStateCard, InstitutionCard, MessageCard, PocketCard } from "../components/SavingPocketCards";
import { DomainSidebar, DomainSidebarHeader, DomainTwoColumnContent, DomainTwoColumnLayout } from "@/components/ui/DomainSidebar";

type InstitutionDraft = { name: string; description: string };
type PocketDraft = {
  saving_institution_id: string;
  name: string;
  amount: string;
  anual_rate: string;
  freeze_time: string;
  starting_date: string;
  description: string;
};

const initialInstitutionDraft: InstitutionDraft = { name: "", description: "" };
const initialPocketDraft: PocketDraft = {
  saving_institution_id: "",
  name: "",
  amount: "",
  anual_rate: "",
  freeze_time: "",
  starting_date: "",
  description: "",
};

export const SavingPocketsView = () => {
  const { t } = useTranslation(["savingPockets", "common", "nav"]);
  const { data: institutions = [], isLoading: institutionsLoading, error: institutionsError } = useSavingInstitutions();
  const { data: pockets = [], isLoading: pocketsLoading, error: pocketsError } = useSavingPockets();
  const updateInstitution = useUpdateSavingInstitution();
  const deleteInstitution = useDeleteSavingInstitution();
  const updatePocket = useUpdateSavingPocket();
  const deletePocket = useDeleteSavingPocket();

  const [institutionDraft, setInstitutionDraft] = useState(initialInstitutionDraft);
  const [pocketDraft, setPocketDraft] = useState(initialPocketDraft);
  const [editingInstitutionId, setEditingInstitutionId] = useState<number | null>(null);
  const [editingPocketId, setEditingPocketId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [freezePreset, setFreezePreset] = useState<FreezePreset>("none");
  const [unifySavings, setUnifySavings] = useState(false);
  const [swipedInstitutionId, setSwipedInstitutionId] = useState<number | null>(null);
  const [swipedPocketId, setSwipedPocketId] = useState<number | null>(null);
  const gestureRef = useRef<{
    id: number | string | null;
    startX: number;
    startY: number;
    action: "tap" | "swipe";
    group: "institution" | "pocket" | null;
  }>({
    id: null,
    startX: 0,
    startY: 0,
    action: "tap",
    group: null,
  });

  useEffect(() => {
    if (!pocketDraft.saving_institution_id && institutions[0]) {
      setPocketDraft((current) => ({ ...current, saving_institution_id: String(institutions[0].id) }));
    }
  }, [institutions, pocketDraft.saving_institution_id]);

  const rows = useMemo(
    () => buildSavingPocketRows(pockets, institutions, t("savingPockets.none")),
    [institutions, pockets, t],
  );
  const {
    totalSaved,
    totalMonthlyReturn,
    activeInstitutions,
    institutionTotals,
    activeInstitutionRows,
    inactiveInstitutionRows,
    nextUnfreeze,
  } = useMemo(() => getSavingPocketOverview(rows, institutions), [institutions, rows]);

  const unifiedRows = useMemo(
    () => buildUnifiedSavingPocketRows(rows),
    [rows],
  );

  const { activePocketRows, inactivePocketRows } = useMemo<{
    activePocketRows: SavingPocketListRow[];
    inactivePocketRows: SavingPocketDisplayRow[];
  }>(() => {
    if (unifySavings) {
      return {
        activePocketRows: unifiedRows,
        inactivePocketRows: [],
      };
    }
    return splitSavingPocketRows(rows);
  }, [rows, unifiedRows, unifySavings]);

  const isLoading = institutionsLoading || pocketsLoading;
  const queryError = institutionsError || pocketsError;
  const isSubmitting =
    updateInstitution.isPending ||
    deleteInstitution.isPending ||
    updatePocket.isPending ||
    deletePocket.isPending;

  const resetInstitutionDraft = () => {
    setInstitutionDraft(initialInstitutionDraft);
    setEditingInstitutionId(null);
    setActionError(null);
  };

  const resetPocketDraft = () => {
    setPocketDraft({ ...initialPocketDraft, saving_institution_id: institutions[0] ? String(institutions[0].id) : "" });
    setEditingPocketId(null);
    setFreezePreset("none");
    setActionError(null);
  };

  const getErrorMessage = (error: unknown) => {
    if (typeof error === "object" && error && "response" in error) {
      const response = (error as { response?: { data?: { error?: string; detail?: string; message?: string } } }).response;
      return response?.data?.detail || response?.data?.error || response?.data?.message || t("savingPockets.loadError");
    }
    return t("savingPockets.loadError");
  };

  const handleInstitutionSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!institutionDraft.name.trim()) return;
    setActionError(null);
    const payload = {
      name: institutionDraft.name.trim(),
      description: institutionDraft.description.trim() || undefined,
      active: true,
    };

    if (!editingInstitutionId) return;

    try {
      await updateInstitution.mutateAsync({ id: editingInstitutionId, data: payload });
      resetInstitutionDraft();
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  };

  const handlePocketSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const saving_institution_id = Number(pocketDraft.saving_institution_id);
    const amount = Number(pocketDraft.amount);
    const anual_rate = Number(pocketDraft.anual_rate);
    if (!saving_institution_id || !pocketDraft.name.trim() || !Number.isFinite(amount)) return;
    setActionError(null);
    const freezeTime = parseFreezeTime(pocketDraft.freeze_time);

    const payload = {
      saving_institution_id,
      name: pocketDraft.name.trim(),
      amount,
      anual_rate: Number.isFinite(anual_rate) ? anual_rate : 0,
      freeze_time: freezeTime,
      freeze_date: freezeTime ? pocketDraft.starting_date : null,
      description: pocketDraft.description.trim() || undefined,
      active: true,
    };

    if (!editingPocketId) return;

    try {
      await updatePocket.mutateAsync({ id: editingPocketId, data: payload });
      resetPocketDraft();
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  };

  const handleEditInstitution = (institution: SavingInstitution) => {
    setActionError(null);
    setEditingInstitutionId(institution.id);
    setInstitutionDraft({ name: institution.name, description: institution.description ?? "" });
  };

  const handleDeleteInstitution = async (institution: SavingInstitution) => {
    const confirmed = await confirmAction({
      title: t("savingPockets.deleteInstitutionPrompt", { name: institution.name }),
      body: t("common:prompts.cannotUndo", { defaultValue: "This action cannot be undone." }),
      confirmLabel: t("common:actions.delete", { defaultValue: "Delete" }),
      cancelLabel: t("common:actions.cancel", { defaultValue: "Cancel" }),
      tone: "danger",
    });
    if (!confirmed) return;
    setActionError(null);
    try {
      await deleteInstitution.mutateAsync(institution.id);
      if (editingInstitutionId === institution.id) resetInstitutionDraft();
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  };

  const handleEditPocket = (pocket: SavingPocket) => {
    setActionError(null);
    setEditingPocketId(pocket.id);
    setPocketDraft({
      saving_institution_id: String(pocket.saving_institution_id),
      name: pocket.name,
      amount: String(pocket.amount),
      anual_rate: String(pocket.anual_rate),
      freeze_time: pocket.freeze_time ? String(pocket.freeze_time) : "",
      starting_date: pocket.freeze_date ?? "",
      description: pocket.description ?? "",
    });
    setFreezePreset(getFreezePreset(pocket.freeze_time ? String(pocket.freeze_time) : ""));
  };

  const handleDeletePocket = async (pocket: SavingPocket) => {
    const confirmed = await confirmAction({
      title: t("savingPockets.deletePocketPrompt", { name: pocket.name }),
      body: t("common:prompts.cannotUndo", { defaultValue: "This action cannot be undone." }),
      confirmLabel: t("common:actions.delete", { defaultValue: "Delete" }),
      cancelLabel: t("common:actions.cancel", { defaultValue: "Cancel" }),
      tone: "danger",
    });
    if (!confirmed) return;
    setActionError(null);
    try {
      await deletePocket.mutateAsync(pocket.id);
      if (editingPocketId === pocket.id) resetPocketDraft();
    } catch (error) {
      setActionError(getErrorMessage(error));
    }
  };

  const handlePressStart = (event: ReactPointerEvent<HTMLButtonElement>, id: number | string, group: "institution" | "pocket") => {
    gestureRef.current = {
      id,
      group,
      startX: event.clientX,
      startY: event.clientY,
      action: "tap",
    };
  };

  const handlePressMove = (event: ReactPointerEvent<HTMLButtonElement>, id: number | string, group: "institution" | "pocket") => {
    if (gestureRef.current.id !== id || gestureRef.current.group !== group) return;

    const deltaX = event.clientX - gestureRef.current.startX;
    const deltaY = event.clientY - gestureRef.current.startY;

    if (Math.abs(deltaX) > 12 && Math.abs(deltaX) > Math.abs(deltaY)) {
      gestureRef.current.action = "swipe";

      if (deltaX < -36) {
        if (group === "institution" && typeof id === "number") setSwipedInstitutionId(id);
        if (group === "pocket" && typeof id === "number") setSwipedPocketId(id);
      } else if (deltaX > 20) {
        if (group === "institution" && swipedInstitutionId === id) setSwipedInstitutionId(null);
        if (group === "pocket" && swipedPocketId === id) setSwipedPocketId(null);
      }
    }
  };

  const handlePressEnd = () => {
    gestureRef.current.id = null;
    gestureRef.current.group = null;
  };

  const handleRowClick = (id: number | string, group: "institution" | "pocket") => {
    if (gestureRef.current.action === "swipe") {
      gestureRef.current.action = "tap";
      return;
    }

    if (group === "institution" && swipedInstitutionId === id) setSwipedInstitutionId(null);
    if (group === "pocket" && swipedPocketId === id) setSwipedPocketId(null);
  };

  const handleActionToggle = (event: ReactMouseEvent<HTMLSpanElement>, id: number | string, group: "institution" | "pocket") => {
    event.preventDefault();
    event.stopPropagation();
    gestureRef.current.action = "tap";
    gestureRef.current.id = null;
    gestureRef.current.group = null;

    if (group === "institution" && typeof id === "number") {
      setSwipedInstitutionId((currentId) => (currentId === id ? null : id));
      setSwipedPocketId(null);
    }

    if (group === "pocket" && typeof id === "number") {
      setSwipedPocketId((currentId) => (currentId === id ? null : id));
      setSwipedInstitutionId(null);
    }
  };

  if (isLoading) {
    return <PageSkeleton lines={4} />;
  }

  return (
    <div className="app-page">
      <div className="app-container max-w-none overflow-x-hidden">
        <div className="relative mx-auto w-full max-w-2xl min-w-0 overflow-x-hidden md:max-w-none">
          <DomainTwoColumnLayout>
            <DomainSidebar>
            <DomainSidebarHeader greeting={t("common:labels.greeting")} title={t("common:nav.savingPockets")} />

            <section className="app-domain-sidebar-section">
              <p className="app-domain-sidebar-label">{t("savingPockets.totalSaved")}</p>
              <p className="app-domain-sidebar-value">
                {formatCurrency(totalSaved)}
              </p>
              <p className="app-domain-sidebar-meta font-medium text-[var(--app-primary)]">
                {formatCurrency(totalMonthlyReturn)} {t("savingPockets.expectedMonthlyReturn")}
              </p>
              <p className="app-domain-sidebar-meta mt-2">
                {nextUnfreeze ? formatDate(nextUnfreeze) : t("savingPockets.none")}
              </p>
            </section>

            <section className="app-domain-sidebar-section">
              <div className="pb-4">
                <h2 className="text-base font-semibold tracking-[-0.01em] text-[var(--app-text)]">{t("savingPockets.institutions")}</h2>
                <p className="app-domain-sidebar-meta mt-2">{activeInstitutions} {t("savingPockets.activeLabel").toLowerCase()} • {formatCurrency(totalSaved)}</p>
              </div>

              {institutions.length === 0 ? (
                <EmptyStateCard title={t("savingPockets.institutions")} body={t("savingPockets.none")} />
              ) : (
                <div>
                  {activeInstitutionRows.map((institution, index) => (
                    <InstitutionCard
                      key={institution.id}
                      institution={institution}
                      total={formatCurrency(institutionTotals[institution.id] ?? 0)}
                      progress={totalSaved > 0 ? ((institutionTotals[institution.id] ?? 0) / totalSaved) * 100 : 0}
                      onEdit={() => handleEditInstitution(institution)}
                      onDelete={() => void handleDeleteInstitution(institution)}
                      bordered={index !== activeInstitutionRows.length - 1}
                      isSwiped={swipedInstitutionId === institution.id}
                      onRowClick={() => handleRowClick(institution.id, "institution")}
                      t={t}
                      onPointerStart={handlePressStart}
                      onPointerMove={handlePressMove}
                      onPointerEnd={handlePressEnd}
                      onActionToggle={handleActionToggle}
                    />
                  ))}

                  {inactiveInstitutionRows.length > 0 ? (
                    <details className="mt-6 group">
                      <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 rounded-full px-1 text-sm">
                        <div className="min-w-0">
                          <p className="font-medium text-[color:color-mix(in_srgb,var(--app-text-secondary)_92%,white)]">{t("savingPockets.inactiveInstitutions", { defaultValue: "Inactive institutions" })}</p>
                          <p className="mt-1 text-[color:color-mix(in_srgb,var(--app-text-secondary)_76%,white)]">{inactiveInstitutionRows.length}</p>
                        </div>
                        <span className="inline-flex items-center gap-1 text-[color:color-mix(in_srgb,var(--app-text-secondary)_72%,white)]">
                          {t("common.actions.viewAll", { defaultValue: "View all" })}
                          <FiChevronRight className="h-3.5 w-3.5 transition-transform group-open:rotate-90" />
                        </span>
                      </summary>
                      <div className="mt-3">
                        {inactiveInstitutionRows.map((institution, index) => (
                          <InstitutionCard
                            key={`inactive-${institution.id}`}
                            institution={institution}
                            total={formatCurrency(institutionTotals[institution.id] ?? 0)}
                            progress={totalSaved > 0 ? ((institutionTotals[institution.id] ?? 0) / totalSaved) * 100 : 0}
                            onEdit={() => handleEditInstitution(institution)}
                            onDelete={() => void handleDeleteInstitution(institution)}
                            bordered={index !== inactiveInstitutionRows.length - 1}
                            isSwiped={swipedInstitutionId === institution.id}
                            onRowClick={() => handleRowClick(institution.id, "institution")}
                            t={t}
                            onPointerStart={handlePressStart}
                            onPointerMove={handlePressMove}
                            onPointerEnd={handlePressEnd}
                            onActionToggle={handleActionToggle}
                          />
                        ))}
                      </div>
                    </details>
                  ) : null}
                </div>
              )}
            </section>

            </DomainSidebar>

            <DomainTwoColumnContent>
            {queryError ? <MessageCard tone="error">{t("savingPockets.loadError")}</MessageCard> : null}
            {actionError && !editingInstitutionId && !editingPocketId ? <MessageCard tone="error">{actionError}</MessageCard> : null}

            <section className="pb-8">
              <div className="pb-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="text-[1.125rem] font-semibold text-[var(--app-text)]">{t("savingPockets.pockets")}</h2>
                  <p className="mt-1 text-base font-semibold text-[var(--app-text-secondary)] tabular-nums">{activePocketRows.length}</p>
                </div>
                <ToggleField
                  label={t("savingPockets.unifySavings")}
                  description={t("savingPockets.unifySavingsHint")}
                  checked={unifySavings}
                  onChange={setUnifySavings}
                />
              </div>

              {activePocketRows.length === 0 ? (
                <EmptyStateCard title={t("savingPockets.pockets")} body={t("savingPockets.none")} large />
              ) : (
                <div className="grid gap-4 xl:grid-cols-2">
                  {activePocketRows.map((row) => {
                    const isPocketRow = row.kind === "pocket";
                    const returnAfterFreeze = isPocketRow ? getReturnAfterFreeze(row) : null;
                    const finalAmount = returnAfterFreeze != null ? row.amount + returnAfterFreeze : row.amount;
                    const freezeTimeLabel = row.freeze_time ? `${row.freeze_time} ${t("savingPockets.days")}` : t("savingPockets.none");
                    const freezeDateLabel = isPocketRow
                      ? formatOptionalDate(parseDateValue(row.freeze_date), t("savingPockets.none"))
                      : formatOptionalDate(getEarliestFreezeDate(row.sourceRows), t("savingPockets.none"));
                    const unfreezeLabel = row.freeze_time
                      ? formatOptionalDate(
                          isPocketRow ? getUnfreezeDate(row) : getLatestUnfreezeDate(row.sourceRows),
                          t("savingPockets.none"),
                        )
                      : t("savingPockets.none");
                    const freezeTimeStatLabel = !isPocketRow && unifySavings ? `${t("savingPockets.freezeTime")} (max)` : t("savingPockets.freezeTime");
                    const freezeDateStatLabel = !isPocketRow && unifySavings ? `${t("savingPockets.startingDate")} (earliest)` : t("savingPockets.startingDate");
                    const unfreezeDateStatLabel = !isPocketRow && unifySavings ? `${t("savingPockets.unfreezeDate")} (latest)` : t("savingPockets.unfreezeDate");

                    return (
                      <PocketCard
                        key={row.id}
                        rowId={row.id}
                        title={row.name}
                        description={row.description ?? ""}
                        amount={formatCurrency(row.amount)}
                        annualRate={`${formatPercent(row.anual_rate)}%`}
                        freezeReturn={isPocketRow && returnAfterFreeze != null ? formatCurrency(returnAfterFreeze) : null}
                        finalAmount={formatCurrency(finalAmount)}
                        monthlyReturn={formatCurrency(Number(row.monthly_return ?? 0))}
                        yearlyReturn={formatCurrency(Number(row.yearly_return ?? 0))}
                        freezeDate={freezeDateLabel}
                        freezeDateLabel={freezeDateStatLabel}
                        freezeTime={freezeTimeLabel}
                        freezeTimeLabel={freezeTimeStatLabel}
                        unfreezeDate={unfreezeLabel}
                        unfreezeDateLabel={unfreezeDateStatLabel}
                        pocketCount={isPocketRow ? null : row.count}
                        pocketCountLabel={t("savingPockets.pocketCount")}
                        onEdit={isPocketRow ? () => handleEditPocket(row) : undefined}
                        onDelete={isPocketRow ? () => void handleDeletePocket(row) : undefined}
                        t={t}
                        bordered={false}
                        isSwiped={isPocketRow ? swipedPocketId === row.id : false}
                        onRowClick={isPocketRow ? () => handleRowClick(row.id, "pocket") : undefined}
                        onPointerStart={handlePressStart}
                        onPointerMove={handlePressMove}
                        onPointerEnd={handlePressEnd}
                        onActionToggle={handleActionToggle}
                      />
                    );
                  })}
                </div>
              )}

              {!unifySavings && inactivePocketRows.length > 0 ? (
                <details className="mt-6 group">
                  <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 rounded-full px-1 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium text-[color:color-mix(in_srgb,var(--app-text-secondary)_92%,white)]">{t("savingPockets.inactivePockets", { defaultValue: "Inactive pockets" })}</p>
                      <p className="mt-1 text-[color:color-mix(in_srgb,var(--app-text-secondary)_76%,white)]">{inactivePocketRows.length}</p>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[color:color-mix(in_srgb,var(--app-text-secondary)_72%,white)]">
                      {t("common.actions.viewAll", { defaultValue: "View all" })}
                      <FiChevronRight className="h-3.5 w-3.5 transition-transform group-open:rotate-90" />
                    </span>
                  </summary>
                  <div className="mt-3 grid gap-4 xl:grid-cols-2">
                    {inactivePocketRows.map((row) => {
                      const returnAfterFreeze = getReturnAfterFreeze(row);
                      const finalAmount = returnAfterFreeze != null ? row.amount + returnAfterFreeze : row.amount;
                      const freezeTimeLabel = row.freeze_time ? `${row.freeze_time} ${t("savingPockets.days")}` : t("savingPockets.none");
                      const freezeDateLabel = formatOptionalDate(parseDateValue(row.freeze_date), t("savingPockets.none"));
                      const unfreezeLabel = row.freeze_time ? formatOptionalDate(getUnfreezeDate(row), t("savingPockets.none")) : t("savingPockets.none");

                      return (
                        <PocketCard
                          key={`inactive-${row.id}`}
                          rowId={row.id}
                          title={row.name}
                          description={row.description ?? ""}
                          amount={formatCurrency(row.amount)}
                          annualRate={`${formatPercent(row.anual_rate)}%`}
                          freezeReturn={returnAfterFreeze != null ? formatCurrency(returnAfterFreeze) : null}
                          finalAmount={formatCurrency(finalAmount)}
                          monthlyReturn={formatCurrency(Number(row.monthly_return ?? 0))}
                          yearlyReturn={formatCurrency(Number(row.yearly_return ?? 0))}
                          freezeDate={freezeDateLabel}
                          freezeDateLabel={t("savingPockets.startingDate")}
                          freezeTime={freezeTimeLabel}
                          freezeTimeLabel={t("savingPockets.freezeTime")}
                          unfreezeDate={unfreezeLabel}
                          unfreezeDateLabel={t("savingPockets.unfreezeDate")}
                          pocketCount={null}
                          pocketCountLabel={t("savingPockets.pocketCount")}
                          onEdit={() => handleEditPocket(row)}
                          onDelete={() => void handleDeletePocket(row)}
                          t={t}
                          bordered={false}
                          isSwiped={swipedPocketId === row.id}
                          onRowClick={() => handleRowClick(row.id, "pocket")}
                          onPointerStart={handlePressStart}
                          onPointerMove={handlePressMove}
                          onPointerEnd={handlePressEnd}
                          onActionToggle={handleActionToggle}
                        />
                      );
                    })}
                  </div>
                </details>
              ) : null}
            </section>

            </DomainTwoColumnContent>

          </DomainTwoColumnLayout>
        </div>
      </div>
      <ModalShell
        isOpen={editingInstitutionId !== null}
        onClose={resetInstitutionDraft}
        title={t("savingPockets.editInstitution")}
        subtitle={t("savingPockets.institutionEditHint")}
        labelledBy="edit-saving-institution-modal-title"
        maxWidthClassName="max-w-[640px]"
        disableClose={isSubmitting}
        onSubmit={handleInstitutionSubmit}
        footer={
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <AppButton tone="secondary" onClick={resetInstitutionDraft} disabled={isSubmitting}>{t("common.actions.cancel")}</AppButton>
            <AppButton type="submit" tone="primary" disabled={isSubmitting || !institutionDraft.name.trim()}>{t("savingPockets.saveInstitution")}</AppButton>
          </div>
        }
      >
        <div className="grid gap-4">
          {actionError && editingInstitutionId ? <MessageCard tone="error">{actionError}</MessageCard> : null}
          <Field id="saving-pocket-edit-institution-name" label={t("savingPockets.institutionName")} value={institutionDraft.name} onChange={(value) => setInstitutionDraft((current) => ({ ...current, name: value }))} placeholder={t("savingPockets.institutionPlaceholder")} />
          <Field id="saving-pocket-edit-institution-description" label={t("savingPockets.description")} value={institutionDraft.description} onChange={(value) => setInstitutionDraft((current) => ({ ...current, description: value }))} placeholder={t("savingPockets.descriptionPlaceholder")} />
        </div>
      </ModalShell>
      <ModalShell
        isOpen={editingPocketId !== null}
        onClose={resetPocketDraft}
        title={t("savingPockets.editPocket")}
        subtitle={t("savingPockets.formHint")}
        labelledBy="edit-saving-pocket-modal-title"
        maxWidthClassName="max-w-[784px]"
        disableClose={isSubmitting}
        onSubmit={handlePocketSubmit}
        footer={
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <AppButton tone="secondary" onClick={resetPocketDraft} disabled={isSubmitting}>{t("common.actions.cancel")}</AppButton>
            <AppButton type="submit" tone="primary" disabled={isSubmitting || institutions.length === 0}>{t("savingPockets.savePocket")}</AppButton>
          </div>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {actionError ? <div className="sm:col-span-2"><MessageCard tone="error">{actionError}</MessageCard></div> : null}
          <SelectField id="saving-pocket-edit-institution" label={t("savingPockets.institution")} value={pocketDraft.saving_institution_id} onChange={(value) => setPocketDraft((current) => ({ ...current, saving_institution_id: value }))} options={institutions.map((item) => ({ label: item.name, value: String(item.id) }))} />
          <Field id="saving-pocket-edit-name" label={t("savingPockets.pocketName")} value={pocketDraft.name} onChange={(value) => setPocketDraft((current) => ({ ...current, name: value }))} placeholder={t("savingPockets.pocketPlaceholder")} />
          <Field id="saving-pocket-edit-amount" label={t("savingPockets.amount")} type="number" value={pocketDraft.amount} onChange={(value) => setPocketDraft((current) => ({ ...current, amount: value }))} placeholder="25000" />
          <Field id="saving-pocket-edit-annual-rate" label={t("savingPockets.annualRate")} type="number" value={pocketDraft.anual_rate} onChange={(value) => setPocketDraft((current) => ({ ...current, anual_rate: value }))} placeholder="12" />
          <FreezeTimeField
            id="saving-pocket-edit-freeze-time"
            label={t("savingPockets.freezeTime")}
            preset={freezePreset}
            value={pocketDraft.freeze_time}
            onPresetChange={(preset) => {
              setFreezePreset(preset);
              setPocketDraft((current) => ({
                ...current,
                freeze_time: preset === "none" ? "" : preset === "other" ? current.freeze_time : preset,
              }));
            }}
            onCustomValueChange={(value) => setPocketDraft((current) => ({ ...current, freeze_time: value }))}
          />
          <Field id="saving-pocket-edit-starting-date" label={t("savingPockets.startingDate")} type="date" value={pocketDraft.starting_date} onChange={(value) => setPocketDraft((current) => ({ ...current, starting_date: value }))} placeholder="" />
          <Field id="saving-pocket-edit-description" label={t("savingPockets.description")} value={pocketDraft.description} onChange={(value) => setPocketDraft((current) => ({ ...current, description: value }))} placeholder={t("savingPockets.descriptionPlaceholder")} />
        </div>
      </ModalShell>
    </div>
  );
};

function getFreezePreset(freezeTime: string): FreezePreset {
  if (!freezeTime) return "none";
  if (FREEZE_PRESET_OPTIONS.includes(freezeTime as FreezePreset)) return freezeTime as FreezePreset;
  return "other";
}

function formatOptionalDate(date: Date | null, fallback: string) {
  return date ? formatDate(date) : fallback;
}
